import http from 'core/http.service';
import BaseService from 'services/BaseService';
import { Page } from 'models/page';
import { DashboardStatsResponse, EstimateStatsResponse, ReceptionDTO, ReceptionSearchDTO } from 'models/reception';
import { PaymentMethod, ReceptionStatus } from 'enums/reception.enum';
import { DashboardDataDTO } from 'models/dashboard';
import moment from 'moment';



class ReceptionService extends BaseService {
    // ===== 기본 CRUD 메소드 =====

    /** 접수 목록 (관리자) */
    public list(search: ReceptionSearchDTO): Promise<Page<ReceptionDTO>> {
        return super.request(http.get('/reception', search));
    }

    /** 접수 상세 (관리자) */
    public get(id: number): Promise<ReceptionDTO> {
        return super.request(http.get(`/reception/${id}`));
    }

    /** 접수 저장 (신규/수정 겸용) */
    public save(dto: ReceptionDTO): Promise<ReceptionDTO> {
        return super.request(http.post('/reception', dto));
    }

    /** 접수 삭제 */
    public remove(id: number): Promise<void> {
        return super.request(http.delete(`/reception/${id}`));
    }

    /** 상태변경 */
    public updateStatus(payload: { id: number; status: ReceptionStatus }): Promise<void> {
        return super.request(http.post('/reception/update-status', { id: payload.id, status: payload.status }));
    }

    /** 결제수단변경 */
    public updatePaymentMethod(payload: { id: number; paymentMethod: PaymentMethod }): Promise<void> {
        return super.request(http.post('/reception/update-payment-method', { id: payload.id, paymentMethod: payload.paymentMethod }));
    }

    public updateInboundDate(id: number): Promise<void> {
        return super.request(http.post(`/reception/update-inbound-date/${id}`, {}));
    }

    public updateOutboundDate(id: number): Promise<void> {
        return super.request(http.post(`/reception/update-outbound-date/${id}`, {}));
    }

    public updateEstimateIssueDate(id: number): Promise<void> {
        return super.request(http.post(`/reception/update-estimate-issue-date/${id}`, {}));
    }

    public getEstimateSummary(search: ReceptionSearchDTO): Promise<{ estimateCount: number; estimateAmount: number; totalCost: number; totalProfit: number; }> {
        return super.request(http.get('/reception/estimate-summary', search));
    }

    public getMenuCount(): Promise<any> {
        return super.request(http.get('/reception/menu-count'));
    }

    // ===== 견적 통계 관련 메소드 =====

    /** 
     * 일일 견적 통계 조회 (견적 관리 화면용)
     * @param date 조회할 날짜 (YYYY-MM-DD 형식, 기본값: 오늘)
     */
    public getDailyEstimateStats(date?: string): Promise<EstimateStatsResponse> {
        return super.request(http.get('/reception/daily-estimate-stats', { date }));
    }

    /** 
     * 월별 견적 통계 조회
     * @param year 조회할 년도
     * @param month 조회할 월 (1-12)
     */
    public getMonthlyEstimateStats(year: number, month: number): Promise<EstimateStatsResponse> {
        return super.request(http.get('/reception/monthly-estimate-stats', { year, month }));
    }

    // ===== 대시보드 통계 관련 메소드 =====
    /**
     * 대시보드 데이터 조회
     */
    async getDashboardData(year: string): Promise<DashboardDataDTO> {
        return super.request(http.get(`/reception/dashboard/${year}`));
    }

    /** 
     * 오늘 견적 통계 조회
     */
    public getTodayEstimateStats(): Promise<EstimateStatsResponse> {
        const today = moment().format('YYYY-MM-DD'); // YYYY-MM-DD 형식
        return this.getDailyEstimateStats(today);
    }

    // 통계
    /** 
     * 지역별 AS현황 통계 조회 (trip-fee 기준)
     * @param year 조회할 년도
     * @param month 조회할 월 (0이면 년도 전체)
     */
    public getRegionAsStats(year: number, month: number): Promise<Array<{ province: string; count: number; amount: number }>> {
        return super.request(http.get('/reception/analytics/region-stats', { year, month }));
    }

    /** 
     * 종류별 AS현황 통계 조회 (reception-item options 기준)
     * @param year 조회할 년도
     * @param month 조회할 월 (0이면 년도 전체)
     */
    public getProductTypeAsStats(year: number, month: number): Promise<{
        chartData: Array<{ productType: string; count: number }>;
        detailStats: {
            totalCount: number;
            freeRepairCount: number;
            totalRevenue: number;
            totalCost: number;
            tripCount: number;
        };
    }> {
        return super.request(http.get('/reception/analytics/product-type-stats', { year, month }));
    }

    /** 
     * 연도별 AS현황 통계 조회
     * @param year 기준 년도 (이 년도 포함 과거 5년간)
     * @param month 조회할 월 (0이면 년도 전체)
     */
    public getYearlyAsStats(year: number, month: number): Promise<{
        chartData: Array<{ year: string; count: number }>;
        detailStats: {
            totalCount: number;
            normalCount: number;
            largeCount: number;
            freeRepairCount: number;
            totalRevenue: number;
            totalCost: number;
            tripCount: number;
        };
    }> {
        return super.request(http.get('/reception/analytics/yearly-stats', { year, month }));
    }

    /** 
     * 월별 수익 상세 통계 조회 (견적금액, 출장비, 수리원가)
     * @param year 조회할 년도
     * @param month 조회할 월 (0이면 년도 전체 - 월별, 양수이면 해당 월의 일별)
     */
    public getMonthlyRevenueDetail(year: number, month: number): Promise<Array<{
        month: number;
        day?: number;
        estimateAmount: number;
        tripFeeAmount: number;
        repairCost: number
    }>> {
        return super.request(http.get('/reception/analytics/monthly-revenue-detail', { year, month }));
    }

    /** 
     * 월별 AS수익 통계 조회 (이익 기준)
     * @param year 조회할 년도
     * @param month 조회할 월 (0이면 년도 전체 - 월별, 양수이면 해당 월의 일별)
     */
    public getMonthlyAsRevenue(year: number, month: number): Promise<Array<{ month: number; day?: number; revenue: number }>> {
        return super.request(http.get('/reception/analytics/monthly-as-revenue', { year, month }));
    }

    /** 
     * 대용량보일러 상세현황 통계 조회
     * @param year 조회할 년도
     * @param month 조회할 월 (0이면 년도 전체)
     */
    public getLargeBoilerStats(year: number, month: number): Promise<Array<{ capacity: string; count: number }>> {
        return super.request(http.get('/reception/analytics/large-boiler-stats', { year, month }));
    }

    /** 
     * 부품상세현황 통계 조회 (일반/대용량 구분)
     * @param year 조회할 년도
     * @param month 조회할 월 (0이면 년도 전체)
     */
    public getPartDetailStats(year: number, month: number): Promise<{
        normalParts: Array<{ partName: string; count: number }>;
        heavyParts: Array<{ partName: string; count: number }>;
    }> {
        return super.request(http.get('/reception/analytics/part-detail-stats', { year, month }));
    }

    async getAgentStats(year: number, month: number): Promise<Array<{
        agent: string;
        tripCount: number;
        holidayCount: number;
        floorCleanCount: number;
        tripFeeAmount: number;
        profit: number;
        freeCount: number;
    }>> {
        return super.request(http.get('/reception/analytics/agent-stats', { year, month }));
    }

}

const receptionService = new ReceptionService();
export default receptionService;